import {
    Accordion,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text
} from '@chakra-ui/react'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import RadioFilter from '../filter-sort/radio-filter'
import CheckboxFilter from '../filter-sort/checkbox-filter'
import {updateFilterValue} from '../filter-sort/utils'
import useProductReviews from '../../bazaarvoice-api/hooks/use-product-reviews'
import {useToast} from '../../hooks/use-toast'
import {API_ERROR_MESSAGE} from '../../constants'
import FilterAccordionItem from '../filter-sort/filter-accordion-item'
import useEffectEvent from '../../hooks/use-effect-event'

const SORT_OPTION_LABELS = defineMessages({
    'SubmissionTime:desc': {
        defaultMessage: 'Most recent',
        id: 'reviews.overlay.sort.option.recent'
    },
    'Helpfulness:desc': {defaultMessage: 'Most helpful', id: 'reviews.overlay.sort.option.helpful'},
    'Rating:asc': {defaultMessage: 'Fewest stars', id: 'reviews.overlay.sort.option.fewest_stars'},
    'Rating:desc': {defaultMessage: 'Most stars', id: 'reviews.overlay.sort.option.most_stars'}
})

export const DEFAULT_SORT_ORDER = 'Rating:desc'

const ReviewsFilterSortOverlay = ({
    isOpen,
    productId,
    query: appliedQuery,
    total,
    onApply: _onApply,
    onClose,
    onReset
}) => {
    const {formatMessage} = useIntl()
    const toast = useToast()
    const sortingOptions = useMemo(
        () =>
            Object.entries(SORT_OPTION_LABELS).map(([value, descriptor]) => ({
                label: formatMessage(descriptor),
                value
            })),
        [formatMessage]
    )
    const ratingOptions = useMemo(
        () =>
            Array.from({length: 5}, (_, idx) => ({
                label: formatMessage(
                    {
                        defaultMessage: '{stars, plural, one {# star} other {# stars}}',
                        id: 'reviews.overlay.filter.rating.option.label'
                    },
                    {stars: idx + 1}
                ),
                value: `${idx + 1}`
            })).reverse(),
        [formatMessage]
    )

    const languageOptions = useMemo(
        () => [
            {
                label: formatMessage({
                    defaultMessage: 'Show only reviews in my language',
                    id: 'reviews.overlay.filter.language.option.label'
                }),
                value: '1'
            }
        ],
        [formatMessage]
    )

    // open all accordion items by default
    const defaultIndex = useMemo(() => [0, 1, 2], [])

    // keep local state of query to avoid applying it until user clicks apply
    const [query, setQuery] = useState(appliedQuery)
    // update local query when applied query changes or overlay is opened
    useEffect(() => {
        setQuery(appliedQuery)
    }, [appliedQuery, isOpen])

    const shouldLoadTotalCount = useMemo(() => {
        const removeSort = (q) => {
            const {sort, ...rest} = q || {}
            return rest
        }

        const filters = removeSort(query)
        const prevFilters = removeSort(appliedQuery)

        const hasFilters = Object.keys(filters).length > 0
        const hadFilters = Object.keys(prevFilters).length > 0

        return hasFilters || hadFilters
    }, [query, appliedQuery])

    const {
        totalCount: _count,
        loading,
        error
    } = useProductReviews(productId, {
        ...query,
        // sort doesn't change the total count, so we can ignore it
        sort: undefined,
        // we only need the total count to show the number of reviews that will be shown
        pageSize: 1,
        enabled: isOpen && shouldLoadTotalCount
    })
    const count = shouldLoadTotalCount ? _count : total

    // update the query when a filter is changed
    const onQueryChange = useCallback((option, {filterId, select, multiple}) => {
        setQuery((query) => updateFilterValue(query, filterId, option, multiple, select))
    }, [])

    const onApply = useCallback(() => {
        _onApply?.(query)
        onClose?.()
    }, [query, _onApply, onClose])

    // log error in console for debugging
    const onError = useEffectEvent((error) => {
        console.error('query error', error)
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    })
    useEffect(() => {
        if (error) {
            onError(error)
        }
    }, [error, onError])

    return (
        <Modal
            colorScheme="stokkeCore"
            isCentered
            isOpen={isOpen}
            motionPreset="slideInBottom"
            scrollBehavior="inside"
            variant="reviewsFilterSortOverlay"
            onClose={onClose}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text textStyle="Regular 2XLarge">
                        <FormattedMessage
                            defaultMessage="Filter & Sort"
                            id="global.filter_and_sort"
                        />
                    </Text>
                    <Text textStyle="Regular Medium" color="stokkeGray.accessibleText">
                        <FormattedMessage
                            id="reviews.overlay.total_text"
                            defaultMessage="{total, plural, one {# review} other {# reviews}}"
                            values={{total: total}}
                        />
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Accordion
                        variant="refinements"
                        allowMultiple
                        reduceMotion
                        defaultIndex={defaultIndex}
                    >
                        {/* Sort options */}
                        <FilterAccordionItem
                            title={formatMessage({
                                defaultMessage: 'Sort By',
                                id: 'reviews.overlay.accordion.title.sort_by'
                            })}
                        >
                            <RadioFilter
                                filterId="sort"
                                options={sortingOptions}
                                selectedOption={query?.sort}
                                onChange={onQueryChange}
                            />
                        </FilterAccordionItem>

                        {/*  Rating options  */}
                        <FilterAccordionItem
                            count={query?.rating?.length}
                            label={formatMessage({
                                defaultMessage: 'Rating',
                                id: 'reviews.overlay.accordion.title.rating'
                            })}
                        >
                            <CheckboxFilter
                                filterId="rating"
                                options={ratingOptions}
                                selectedOptions={query?.rating}
                                onChange={onQueryChange}
                            />
                        </FilterAccordionItem>

                        {/*  Language option  */}
                        <FilterAccordionItem
                            count={query?.language ? 1 : 0}
                            label={formatMessage({
                                defaultMessage: 'Language',
                                id: 'reviews.overlay.accordion.title.language'
                            })}
                        >
                            <CheckboxFilter
                                filterId="language"
                                options={languageOptions}
                                selectedOptions={query?.language}
                                onChange={onQueryChange}
                            />
                        </FilterAccordionItem>
                    </Accordion>
                </ModalBody>

                <ModalFooter>
                    <Flex direction="column" align="center" gap="6" width="full">
                        <Button variant="link" onClick={onReset}>
                            <FormattedMessage
                                defaultMessage="Clear all"
                                id="reviews.overlay.button.clear"
                            />
                        </Button>
                        <Flex direction="column" align="center" gap="2" width="full">
                            <Button
                                alignSelf="stretch"
                                width="full"
                                onClick={onApply}
                                isDisabled={loading || count === 0}
                                isLoading={loading}
                                data-testid="applyButton"
                            >
                                {count === 0 ? (
                                    <FormattedMessage
                                        defaultMessage="No reviews"
                                        id="reviews.overlay.button.no_reviews"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="See {count, plural, one {# Review} other {# Reviews}}"
                                        id="reviews.overlay.button.apply"
                                        values={{count: count != null ? count : total}}
                                    />
                                )}
                            </Button>
                            {count === 0 && (
                                <Text textStyle="Regular Small" color="stokkeGray.accessibleText">
                                    <FormattedMessage
                                        defaultMessage="Unfortunately, no reviews matching the selected filters were found."
                                        id="reviews.overlay.no_reviews"
                                    />
                                </Text>
                            )}
                        </Flex>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

ReviewsFilterSortOverlay.propTypes = {
    count: PropTypes.number,
    isOpen: PropTypes.bool,
    isLoading: PropTypes.bool,
    productId: PropTypes.string,
    query: PropTypes.object,
    total: PropTypes.number,
    onApply: PropTypes.func,
    onClose: PropTypes.func,
    onReset: PropTypes.func
}

export default ReviewsFilterSortOverlay
